import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { DARK_MODE } from '../../constants';
import RepunchModal from '../RePunchModal/RePunchModal';
import ReengraveModal from '../ReEngraveModal.js/ReEngraveModal';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ReEngraveIcon from '../../assets/images/Re-Engrave-Icon.png';
import ViewDocumentIcon from '../../assets/images/View-document-icon.png';
import DefactiveEntryIcon from '../../assets/images/Defactive-Entry.png';
import ViewDocModal from '../ViewDocModal/ViewDocModal';
import ReasonAndRemarkModal from '../ReportModal/ReasonAndRemarkModal';
import ReprintBarcodeModal from '../ReprintBarcodeModal/ReprintBarcodeModal';
import VinBarcodeIcon from '../../assets/images/Vin_Barcode.png';
import PrintBarcodeModal from '../PrintBarcodeModal/PrintBarcodeModal';
import WtclOkTagTestModal from '../WtclOkTagTestModal/WtclOkTagTestModal';

// ─── Reusable Sidebar Card ────────────────────────────────────────────────────
const SidebarCard = ({ icon, label, onClick }) => (
	<Box
		onClick={onClick}
		sx={{
			width: '160px',
			minHeight: '160px',
			backgroundColor: '#fff',
			borderRadius: '10px',
			boxShadow: '0px 4px 4px rgba(0,0,0,0.25)',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: '10px',
			cursor: 'pointer',
			padding: '14px 10px',
			mb: 3,
			transition: 'transform 0.15s ease, box-shadow 0.15s ease',
			'&:hover': {
				transform: 'scale(1.03)',
				boxShadow: '0px 6px 12px rgba(0,0,0,0.3)',
			},
			// 4K screens
			'@media (min-width: 3840px)': {
				width: '360px',
				minHeight: '360px',
				borderRadius: '25px',
				mb: 6,
				gap: '20px',
				padding: '28px 20px',
			},
		}}
	>
		{/* Icon */}
		<Box
			component="img"
			src={icon}
			alt={label}
			sx={{
				width: '72px',
				height: '72px',
				objectFit: 'contain',
				flexShrink: 0,
				'@media (min-width: 3840px)': {
					width: '180px',
					height: '180px',
				},
			}}
		/>

		{/* Label */}
		<Typography
			sx={{
				fontFamily: 'JLR Emeric, sans-serif',
				fontSize: '13px',
				fontWeight: 800,
				color: '#000',
				textAlign: 'center',
				lineHeight: 1.3,
				width: '100%',
				wordBreak: 'break-word',
				'@media (min-width: 3840px)': {
					fontSize: '28px',
				},
			}}
		>
			{label}
		</Typography>
	</Box>
);

// ─── VirtualSidebar ───────────────────────────────────────────────────────────
const VirtualSidebar = () => {
	const theme = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const [showTrigger, setShowTrigger] = useState(false);
	const sidebarRef = useRef(null);
	const mouseTimeoutRef = useRef(null);

	const [isRepunchModalOpen, setIsRepunchModalOpen] = useState(false);
	const [isReengraveModalOpen, setIsReengraveModalOpen] = useState(false);
	const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
	const [isPrintOpen, setIsPrintOpen] = useState(false);
	const [isCustomPrintOpen, setIsCustomPrintOpen] = useState(false);
	const [isOkTagTestModalOpen, setIsOkTagTestModalOpen] = useState(false);

	const allowedOperations =
		JSON.parse(localStorage.getItem('operations') || '{}')
			?.allowedOperations ?? [];

	// Show trigger button when mouse near right edge
	useEffect(() => {
		const handleMouseMove = (e) => {
			const nearEdge = e.clientX > window.innerWidth - 50;
			if (nearEdge && !isOpen) {
				setShowTrigger(true);
				clearTimeout(mouseTimeoutRef.current);
			} else if (!nearEdge && !isOpen) {
				mouseTimeoutRef.current = setTimeout(() => setShowTrigger(false), 500);
			}
		};
		document.addEventListener('mousemove', handleMouseMove);
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			clearTimeout(mouseTimeoutRef.current);
		};
	}, [isOpen]);

	const cards = [
		{
			icon: ViewDocumentIcon,
			label: 'View Documents',
			onClick: () => setIsRepunchModalOpen(true),
			show: true,
		},
		{
			icon: ReEngraveIcon,
			label: 'Re Engrave',
			onClick: () => setIsReengraveModalOpen(true),
			show: allowedOperations.includes('ENGRAVE'),
		},
		{
			icon: VinBarcodeIcon,
			label: 'Print Barcode',
			onClick: () => setIsPrintModalOpen(true),
			show: true,
		},
		{
			icon: VinBarcodeIcon,
			label: 'Special Character Print Barcode',
			onClick: () => setIsPrintOpen(true),
			show: true,
		},
		{
			icon: VinBarcodeIcon,
			label: 'Custom Barcode Print',
			onClick: () => setIsCustomPrintOpen(true),
			show: true,
		},
		{
			icon: VinBarcodeIcon,
			label: 'OK Tag Re-Print',
			onClick: () => setIsOkTagTestModalOpen(true),
			show: true,
		},
	];

	return (
		<>
			{/* ── Trigger Button ─────────────────────────────────────── */}
			{showTrigger && (
				<Box
					sx={{
						position: 'fixed',
						right: 0,
						top: '50%',
						transform: 'translateY(-50%)',
						zIndex: 1000,
						cursor: 'pointer',
						'@media (min-width: 3840px)': { top: '50%' },
					}}
					onClick={() => setIsOpen((prev) => !prev)}
					onMouseEnter={() => setShowTrigger(true)}
					onMouseLeave={() => setShowTrigger(false)}
				>
					<Box
						sx={{
							width: '32px',
							height: '48px',
							backgroundColor: '#ffd000',
							borderRadius: '20px 0 0 20px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#000',
							transition: 'width 0.2s',
							'&:hover': { width: '38px' },
							'@media (min-width: 3840px)': {
								width: '70px',
								height: '100px',
								borderRadius: '40px 0 0 40px',
							},
						}}
					>
						<ChevronLeftIcon
							sx={{
								transform: isOpen ? 'scaleX(1)' : 'scaleX(-1)',
								fontSize: '22px',
								transition: 'transform 0.3s',
								'@media (min-width: 3840px)': { fontSize: '48px' },
							}}
						/>
					</Box>
				</Box>
			)}

			{/* ── Sidebar Panel ──────────────────────────────────────── */}
			<Box
				ref={sidebarRef}
				sx={{
					position: 'fixed',
					right: isOpen ? '0px' : '-200px',
					top: 0,
					width: '195px',
					height: '100%',
					backgroundColor: '#0F107B',
					borderRadius: '8px 0 0 8px',
					boxShadow: '-4px 0px 16px rgba(0,0,0,0.35)',
					transition: 'right 0.3s ease-in-out',
					zIndex: 999,
					// Scrollable card list
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					overflowY: 'auto',
					overflowX: 'hidden',
					paddingTop: '24px',
					paddingBottom: '24px',
					scrollbarWidth: 'none',       // Firefox
					'&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
					'@media (min-width: 3840px)': {
						right: isOpen ? '0px' : '-460px',
						width: '450px',
						paddingTop: '60px',
						paddingBottom: '60px',
					},
				}}
				onMouseEnter={() => {
					setIsOpen(true);
					setShowTrigger(true);
				}}
				onMouseLeave={() => {
					setIsOpen(false);
					setTimeout(() => setShowTrigger(false), 300);
				}}
			>
				{cards
					.filter((c) => c.show)
					.map((card, i) => (
						<SidebarCard
							key={i}
							icon={card.icon}
							label={card.label}
							onClick={card.onClick}
						/>
					))}
			</Box>

			{/* ── Modals ─────────────────────────────────────────────── */}
			{isRepunchModalOpen && (
				<ViewDocModal
					open={isRepunchModalOpen}
					onClose={() => setIsRepunchModalOpen(false)}
					title="Documents"
				/>
			)}
			{isReengraveModalOpen && (
				<ReengraveModal
					open={isReengraveModalOpen}
					handleClose={() => setIsReengraveModalOpen(false)}
				/>
			)}
			{isPrintModalOpen && (
				<ReprintBarcodeModal
					handleClose={() => setIsPrintModalOpen(false)}
				/>
			)}
			{isPrintOpen && (
				<PrintBarcodeModal
					handleClose={() => setIsPrintOpen(false)}
				/>
			)}
			{isCustomPrintOpen && (
				<PrintBarcodeModal
					handleClose={() => setIsCustomPrintOpen(false)}
					initialTab="custom"
				/>
			)}
			{isOkTagTestModalOpen && (
				<WtclOkTagTestModal
					handleClose={() => setIsOkTagTestModalOpen(false)}
				/>
			)}
		</>
	);
};

export default VirtualSidebar;